import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { AlertCircle, FileText, ArrowRight } from 'lucide-react';
import { formsApi } from '../api/api';
import { Form } from '../types/form';

const AnonymousAccess: React.FC = () => {
  const { accessToken } = useParams<{ accessToken: string }>();
  const navigate = useNavigate();
  const [forms, setForms] = useState<Form[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAccessibleForms = async () => {
      if (!accessToken) {
        setError('Invalid access token');
        setLoading(false);
        return;
      }

      try {
        const response = await formsApi.get(`/anonymous/${accessToken}/forms/`);
        setForms(response.data.forms || []);
      } catch (err: any) {
        setError(err.response?.data?.error || 'Failed to load accessible forms');
      } finally {
        setLoading(false);
      }
    };

    fetchAccessibleForms();
  }, [accessToken]);

  const handleFormSelect = (formId: string) => {
    navigate(`/anonymous/${accessToken}/forms/${formId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-foreground"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2 text-destructive">
              <AlertCircle className="w-5 h-5" />
              <span>Access Error</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-6 py-4">
          <h1 className="text-2xl font-bold text-foreground">Available Forms</h1>
          <p className="text-muted-foreground">
            Select a form to fill out
          </p>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8">
        {forms.length === 0 ? (
          <Card className="w-full max-w-md mx-auto">
            <CardContent className="pt-6">
              <div className="text-center">
                <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  No Forms Available
                </h3>
                <p className="text-muted-foreground">
                  There are no forms available for your access level.
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {forms.map((form) => (
              <Card
                key={form.id}
                className="cursor-pointer transition-colors hover:bg-muted/50"
                onClick={() => handleFormSelect(form.id)}
              >
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span className="truncate">{form.title}</span>
                    <ArrowRight className="w-5 h-5 text-muted-foreground" />
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {form.description && (
                    <p className="text-muted-foreground line-clamp-3">
                      {form.description}
                    </p>
                  )}
                  <div className="mt-4">
                    <Button 
                      className="w-full"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleFormSelect(form.id);
                      }}
                    >
                      Fill Form
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default AnonymousAccess;